if (!customElements.get('product-modal')) {
  customElements.define(
    'product-modal',
    class ProductModal extends ModalDialog {
      constructor() {
        super();

        /* Desktop resolution-aware click zoom */
        this.addEventListener('click', (event) => {
          if (!window.matchMedia('(min-width: 750px)').matches) return;

          const image = event.target.closest(
            '.product-media-modal__content img'
          );

          if (!image) return;

          event.preventDefault();
          event.stopPropagation();

          /* Second click: zoom out */
          if (image.classList.contains('product-modal-image--zoomed')) {
            image.classList.remove('product-modal-image--zoomed');
            image.style.removeProperty('--product-modal-zoom');
            return;
          }

          const rect = image.getBoundingClientRect();

          if (!rect.width || !rect.height) return;

          /*
          * Maximum zoom without enlarging beyond the image's
          * original pixel dimensions.
          */
          const widthScale = image.naturalWidth / rect.width;
          const heightScale = image.naturalHeight / rect.height;
          const nativeScale = Math.min(widthScale, heightScale);

          /*
          * Never exceed 1.8×.
          * Smaller images automatically receive less zoom.
          */
          const zoomLevel = Math.min(1.4, Math.max(1, nativeScale));

          /*
          * Image is already being displayed close to its
          * original resolution, so extra zoom would look blurry.
          */
          if (zoomLevel <= 1.05) return;

          image.style.setProperty(
            '--product-modal-zoom',
            zoomLevel.toFixed(3)
          );

          image.classList.add('product-modal-image--zoomed');
        });

        /* Mobile pinch zoom state */
        this.mobileZoom = {
          image: null,
          pointers: new Map(),

          scale: 1,
          x: 0,
          y: 0,

          startScale: 1,
          startX: 0,
          startY: 0,

          startDistance: 0,
          startCenterX: 0,
          startCenterY: 0,

          dragStartX: 0,
          dragStartY: 0,
        };

        this.addEventListener(
          'pointerdown',
          this.handleMobilePointerDown.bind(this)
        );

        this.addEventListener(
          'pointermove',
          this.handleMobilePointerMove.bind(this)
        );

        this.addEventListener(
          'pointerup',
          this.handleMobilePointerEnd.bind(this)
        );

        this.addEventListener(
          'pointercancel',
          this.handleMobilePointerEnd.bind(this)
        );
      }

      getPointerDistance() {
        const pointers = Array.from(
          this.mobileZoom.pointers.values()
        );

        if (pointers.length < 2) return 0;

        return Math.hypot(
          pointers[1].x - pointers[0].x,
          pointers[1].y - pointers[0].y
        );
      }

      getPointerCenter() {
        const pointers = Array.from(
          this.mobileZoom.pointers.values()
        );

        if (pointers.length < 2) {
          return {
            x: 0,
            y: 0,
          };
        }

        return {
          x: (pointers[0].x + pointers[1].x) / 2,
          y: (pointers[0].y + pointers[1].y) / 2,
        };
      }

      handleMobilePointerDown(event) {
        if (!window.matchMedia('(max-width: 749px)').matches) return;

        const image = event.target.closest(
          '.product-media-modal__content img'
        );

        if (!image) return;

        const state = this.mobileZoom;

        if (state.image && state.image !== image) {
          this.resetMobileZoom();
        }

        state.image = image;

        state.pointers.set(event.pointerId, {
          x: event.clientX,
          y: event.clientY,
        });

        image.setPointerCapture?.(event.pointerId);

        if (state.pointers.size === 1) {
          state.startX = state.x;
          state.startY = state.y;

          state.dragStartX = event.clientX;
          state.dragStartY = event.clientY;

          if (state.scale > 1) {
            image.classList.add('mobile-image--dragging');
          }
        }

        if (state.pointers.size === 2) {
          const center = this.getPointerCenter();

          state.startDistance = this.getPointerDistance();
          state.startScale = state.scale;

          state.startX = state.x;
          state.startY = state.y;

          state.startCenterX = center.x;
          state.startCenterY = center.y;
        }
      }

      handleMobilePointerMove(event) {
        if (!window.matchMedia('(max-width: 749px)').matches) return;

        const state = this.mobileZoom;

        if (
          !state.image ||
          !state.pointers.has(event.pointerId)
        ) {
          return;
        }

        event.preventDefault();

        state.pointers.set(event.pointerId, {
          x: event.clientX,
          y: event.clientY,
        });

        /* Two-finger pinch */
        if (state.pointers.size === 2) {
          const distance = this.getPointerDistance();
          const center = this.getPointerCenter();

          if (!state.startDistance) return;

          state.scale = Math.min(
            4,
            Math.max(
              1,
              state.startScale *
                (distance / state.startDistance)
            )
          );

          state.x =
            state.startX +
            (center.x - state.startCenterX);

          state.y =
            state.startY +
            (center.y - state.startCenterY);

          if (state.scale <= 1) {
            state.scale = 1;
            state.x = 0;
            state.y = 0;
          }

          this.limitMobileMovement();
          this.applyMobileZoom();

          return;
        }

        /* One-finger drag while zoomed */
        if (state.pointers.size === 1 && state.scale > 1) {
          state.x =
            state.startX +
            (event.clientX - state.dragStartX);

          state.y =
            state.startY +
            (event.clientY - state.dragStartY);

          this.limitMobileMovement();
          this.applyMobileZoom();
        }
      }

      handleMobilePointerEnd(event) {
        const state = this.mobileZoom;

        if (!state.pointers.has(event.pointerId)) return;

        state.pointers.delete(event.pointerId);

        if (state.image) {
          state.image.classList.remove(
            'mobile-image--dragging'
          );
        }

        /* Continue dragging smoothly after one finger leaves */
        if (state.pointers.size === 1) {
          const remainingPointer = Array.from(
            state.pointers.values()
          )[0];

          state.startX = state.x;
          state.startY = state.y;

          state.dragStartX = remainingPointer.x;
          state.dragStartY = remainingPointer.y;
        }

        if (state.pointers.size === 0 && state.scale <= 1) {
          this.resetMobileZoom();
        }
      }

      limitMobileMovement() {
        const state = this.mobileZoom;

        if (!state.image || state.scale <= 1) {
          state.x = 0;
          state.y = 0;
          return;
        }

        const maxX =
          (state.image.clientWidth * (state.scale - 1)) / 2;

        const maxY =
          (state.image.clientHeight * (state.scale - 1)) / 2;

        state.x = Math.max(
          -maxX,
          Math.min(maxX, state.x)
        );

        state.y = Math.max(
          -maxY,
          Math.min(maxY, state.y)
        );
      }

      applyMobileZoom() {
        const state = this.mobileZoom;

        if (!state.image) return;

        state.image.style.transform =
          `translate3d(${state.x}px, ${state.y}px, 0) scale(${state.scale})`;

        state.image.classList.toggle(
          'mobile-image--zoomed',
          state.scale > 1
        );
      }

      resetMobileZoom() {
        const state = this.mobileZoom;

        if (state?.image) {
          state.image.style.transform = '';

          state.image.classList.remove(
            'mobile-image--zoomed',
            'mobile-image--dragging'
          );
        }

        if (!state) return;

        state.image = null;
        state.pointers.clear();

        state.scale = 1;
        state.x = 0;
        state.y = 0;

        state.startScale = 1;
        state.startX = 0;
        state.startY = 0;

        state.startDistance = 0;
        state.startCenterX = 0;
        state.startCenterY = 0;

        state.dragStartX = 0;
        state.dragStartY = 0;
      }

      hide() {
        this.querySelectorAll(
          '.product-modal-image--zoomed'
        ).forEach((image) => {
          image.classList.remove(
            'product-modal-image--zoomed'
          );
        });

        this.resetMobileZoom();

        super.hide();
      }

      show(opener) {
        this.querySelectorAll(
          '.product-modal-image--zoomed'
        ).forEach((image) => {
          image.classList.remove(
            'product-modal-image--zoomed'
          );
        });

        this.resetMobileZoom();

        super.show(opener);
        this.showActiveMedia();
      }

      showActiveMedia() {
        this.querySelectorAll(
          `[data-media-id]:not([data-media-id="${this.openedBy.getAttribute(
            'data-media-id'
          )}"])`
        ).forEach((element) => {
          element.classList.remove('active');
        });

        const activeMedia = this.querySelector(
          `[data-media-id="${this.openedBy.getAttribute(
            'data-media-id'
          )}"]`
        );

        const activeMediaTemplate =
          activeMedia.querySelector('template');

        const activeMediaContent = activeMediaTemplate
          ? activeMediaTemplate.content
          : null;

        activeMedia.classList.add('active');
        activeMedia.scrollIntoView();

        const container = this.querySelector(
          '[role="document"]'
        );

        container.scrollLeft =
          (activeMedia.width - container.clientWidth) / 2;

        if (
          activeMedia.nodeName === 'DEFERRED-MEDIA' &&
          activeMediaContent &&
          activeMediaContent.querySelector('.js-youtube')
        ) {
          activeMedia.loadContent();
        }
      }
    }
  );
}